import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePagination } from '../hooks/usePagination';
import Pagination from './PaginationNew';
import { TripService } from '../services/tripService';
import type { TripRequest } from '../types/trip';

const TripRequestsTable: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isAdmin = user?.role === 'admin';

  const { currentPage, itemsPerPage, setPage, setLimit } = usePagination({
    defaultPage: 1,
    defaultLimit: 50,
  });

  const [rows, setRows] = useState<TripRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  useEffect(() => {
    document.title = currentPage > 1 ? `Trip Requests - Page ${currentPage}` : 'Trip Requests';
  }, [currentPage]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 400);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!isAdmin) return;

    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await TripService.listTripRequests({
          page: currentPage,
          limit: itemsPerPage,
          sort: '-createdAt',
          search: debouncedSearch.trim() || undefined,
        });

        if (!isMounted) return;
        setRows(res.items || []);
        setTotalPages(res.pages || 1);
        setTotalItems(typeof res.total === 'number' ? res.total : (res.items || []).length);
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load trip requests');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isAdmin, currentPage, itemsPerPage, debouncedSearch]);

  const formatDate = (iso: string | undefined | null) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };

  const formatDateValue = (value: unknown) => {
    if (!value) return '—';
    if (typeof value === 'string') return formatDate(value);
    if (typeof value === 'object' && value && '$date' in (value as any) && typeof (value as any).$date === 'string') {
      return formatDate((value as any).$date);
    }
    return '—';
  };

  const formatCell = (value: unknown) => {
    if (value == null) return '—';
    if (typeof value === 'string') return value || '—';
    if (typeof value === 'number') return Number.isFinite(value) ? value.toLocaleString('en-IN') : String(value);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object' && value && '$date' in (value as any) && typeof (value as any).$date === 'string') {
      return String((value as any).$date);
    }
    return '—';
  };

  const getRequestData = (r: TripRequest): any => {
    const d: any = (r as any).data;
    return d && typeof d === 'object' ? d : null;
  };

  const getTripId = (r: TripRequest): string | null => {
    const d = getRequestData(r);
    const fromData = d?.tripId;
    if (typeof fromData === 'string' && fromData) return fromData;

    const v: any = (r as any).tripId;
    if (!v) return null;
    if (typeof v === 'string') return v;
    if (typeof v === 'object' && typeof v._id === 'string') return v._id;
    return null;
  };

  const getUserLabel = (r: TripRequest): string => {
    const v: any = (r as any).userId ?? (r as any).user;
    if (!v) return '—';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') {
      const name = typeof v.name === 'string' ? v.name.trim() : '';
      const email = typeof v.email === 'string' ? v.email.trim() : '';
      const id = typeof v._id === 'string' ? v._id : '';
      if (name && email) return `${name} (${email})`;
      if (name) return name;
      if (email) return email;
      if (id) return id;
    }
    return '—';
  };

  const getTripLabel = (r: TripRequest): string => {
    const d = getRequestData(r);
    if (d?.tripName) return String(d.tripName);
    if ((r as any).tripName) return String((r as any).tripName);

    if (d?.destinationAirport) return String(d.destinationAirport);
    if ((r as any).destination) return String((r as any).destination);

    const v: any = (r as any).tripId;
    if (!v) return '—';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') {
      const name = typeof v.name === 'string' ? v.name : typeof v.title === 'string' ? v.title : '';
      const id = typeof v._id === 'string' ? v._id : '';
      return name || id || '—';
    }
    return '—';
  };

  const toText = (value: unknown): string => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const escapeCsvValue = (value: unknown): string => {
    const safe = toText(value).replace(/\r?\n|\r/g, ' ');
    return `"${safe.replace(/"/g, '""')}"`;
  };

  const fetchAllTripRequests = async (): Promise<TripRequest[]> => {
    const limit = 500;
    let page = 1;
    const all: TripRequest[] = [];
    const searchTerm = (debouncedSearch || search).trim();

    // Pull every page so CSV contains the full dataset (respecting current search filter)
    while (true) {
      const res = await TripService.listTripRequests({
        page,
        limit,
        sort: '-createdAt',
        search: searchTerm ? searchTerm : undefined,
      });

      const items = res.items || [];
      all.push(...items);

      const reachedEnd = page >= (res.pages || 1) || items.length < limit;
      if (reachedEnd) break;
      page += 1;
    }

    return all;
  };

  const buildCsvRow = (r: TripRequest) => {
    const data = getRequestData(r);

    return {
      id: r._id,
      tripId: getTripId(r) ?? '',
      tripName: getTripLabel(r),
      originAirport: formatCell(data?.originAirport ?? (r as any).originAirport),
      destinationAirport: formatCell(data?.destinationAirport ?? (r as any).destinationAirport),
      hotelType: formatCell(data?.hotelType ?? (r as any).hotelType),
      dateFrom: formatCell(data?.dateFrom ?? (r as any).dateFrom),
      dateTo: formatCell(data?.dateTo ?? (r as any).dateTo),
      passengers: formatCell(data?.passengers ?? (r as any).passengers ?? (r as any).numberOfTravelers),
      infants: formatCell(data?.infants ?? data?.children ?? (r as any).infants),
      children: formatCell(data?.children ?? (r as any).children),
      travelClass: formatCell(data?.travelClass ?? (r as any).travelClass),
      mobile: formatCell(data?.mobile ?? (r as any).mobile),
      rewardPoints: formatCell(data?.rewardPoints ?? (r as any).rewardPoints),
      user: getUserLabel(r),
      createdAt: formatDateValue((r as any).createdAt),
      updatedAt: formatDateValue((r as any).updatedAt),
      rawData: data ? JSON.stringify(data) : '',
    };
  };

  const handleDownloadCsv = async () => {
    if (downloading) return;

    setDownloadError(null);
    setDownloading(true);

    try {
      const allRequests = await fetchAllTripRequests();

      if (!allRequests.length) {
        setDownloadError('No trip requests available to download.');
        return;
      }

      const rowsForCsv = allRequests.map(buildCsvRow);
      const columns = [
        { key: 'id', label: 'ID' },
        { key: 'tripId', label: 'Trip ID' },
        { key: 'tripName', label: 'Trip Name' },
        { key: 'originAirport', label: 'Origin Airport' },
        { key: 'destinationAirport', label: 'Destination Airport' },
        { key: 'hotelType', label: 'Hotel Type' },
        { key: 'dateFrom', label: 'Date From' },
        { key: 'dateTo', label: 'Date To' },
        { key: 'passengers', label: 'Passengers' },
        { key: 'infants', label: 'Infants' },
        { key: 'children', label: 'Children' },
        { key: 'travelClass', label: 'Class' },
        { key: 'mobile', label: 'Mobile' },
        { key: 'rewardPoints', label: 'Reward Points' },
        { key: 'user', label: 'User' },
        { key: 'createdAt', label: 'Created' },
        { key: 'updatedAt', label: 'Updated' },
        { key: 'rawData', label: 'Raw Data' },
      ] as const;

      const csvLines = [
        columns.map((c) => c.label).join(','),
        ...rowsForCsv.map((row) =>
          columns.map((c) => escapeCsvValue((row as Record<string, unknown>)[c.key])).join(',')
        ),
      ];

      const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `trip-requests-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Failed to download CSV');
    } finally {
      setDownloading(false);
    }
  };

  const safeRows = useMemo(() => rows, [rows]);

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-12 h-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-500 mb-4">You need admin privileges to access Trip Requests.</p>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (loading && safeRows.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="mt-2 text-gray-500">Loading trip requests...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trip Requests</h1>
          <p className="mt-1 text-sm text-gray-500">Admin list (GET /api/trips/request)</p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={handleDownloadCsv}
            disabled={downloading}
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
              downloading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {downloading ? 'Preparing CSV...' : 'Download CSV'}
          </button>

          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (currentPage !== 1) setPage(1);
              }}
              className="block w-80 pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>}
      {downloadError && <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md p-4 text-sm">{downloadError}</div>}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trip Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Origin</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Destination</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hotel</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Passengers</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Infants</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reward Points</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {safeRows.map((r) => (
                <tr
                  key={r._id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/trips/requests/${encodeURIComponent(r._id)}`)}
                >
                  <td className="px-4 py-4">
                    <div className="text-sm font-medium text-gray-900">{getTripLabel(r)}</div>
                    <div className="text-xs text-gray-500 break-all">{r._id}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCell(getRequestData(r)?.originAirport ?? (r as any).originAirport)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCell(getRequestData(r)?.destinationAirport ?? (r as any).destinationAirport)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCell(getRequestData(r)?.hotelType ?? (r as any).hotelType)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCell(getRequestData(r)?.dateFrom ?? (r as any).dateFrom)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCell(getRequestData(r)?.dateTo ?? (r as any).dateTo)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCell(getRequestData(r)?.passengers ?? (r as any).passengers ?? (r as any).numberOfTravelers)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCell(getRequestData(r)?.infants ?? getRequestData(r)?.children ?? (r as any).infants)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCell(getRequestData(r)?.travelClass ?? (r as any).travelClass)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCell(getRequestData(r)?.mobile ?? (r as any).mobile)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCell(getRequestData(r)?.rewardPoints ?? (r as any).rewardPoints)}</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">{getUserLabel(r)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDateValue((r as any).createdAt)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDateValue((r as any).updatedAt)}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {getTripId(r) ? (
                      <button
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        onClick={() => navigate(`/trips/${encodeURIComponent(String(getTripId(r)))}`)}
                      >
                        View Trip
                      </button>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}

              {!loading && safeRows.length === 0 && (
                <tr>
                  <td colSpan={15} className="px-6 py-10 text-center text-sm text-gray-500">
                    No trip requests found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setPage}
        onLimitChange={setLimit}
      />
    </div>
  );
};

export default TripRequestsTable;
